import { useAuthInfo } from "../../context/AuthContext";
import { sendFeedbackToSlack } from "../../utils/slack";
import { type FeedbackItem, type NewFeedbackItem, deleteFeedback, findFeedback, saveFeedback, updateFeedback } from "../../utils/web-api";
import { useCallback, useEffect, useState } from "react";

export interface UseRuleFeedbackInputs {
  selectedRule: any;
  currentReviewId: string;
  authHeader: string;
  selectedRuleUuid: string;
}

export function useRuleFeedback({ selectedRule, currentReviewId, authHeader, selectedRuleUuid }: UseRuleFeedbackInputs) {
    const { user } = useAuthInfo();
    const [showFeedback, setShowFeedback] = useState(false);
    const [feedbackItem, setFeedbackItem] = useState<NewFeedbackItem | FeedbackItem | null>();
    useEffect(() => {
        if (selectedRule !== undefined) {
            const fetchExistingFeedback = async () => {
                const ruleUuid = selectedRule?.rule?.uuid;
                if (currentReviewId && ruleUuid && authHeader) {
                    const existing = await findFeedback(authHeader, currentReviewId, ruleUuid);
                    if (existing) {
                        setFeedbackItem(existing);
                    }
                }
            };

            fetchExistingFeedback();
        }

        return () => {
            setFeedbackItem(null);
        };
    }, [selectedRuleUuid, currentReviewId]);
    const submitFeedback = useCallback(async () => {
        try {
            let feedback: FeedbackItem;
            if ("id" in feedbackItem) {
                feedback = await updateFeedback(authHeader, feedbackItem.id, feedbackItem);
                sendFeedbackToSlack(authHeader, feedback, user, selectedRule);
            } else {
                feedback = await saveFeedback(authHeader, feedbackItem);
                sendFeedbackToSlack(authHeader, feedback, user, selectedRule);
            }

            if (feedback) {
                setFeedbackItem(feedback);
            }
        }
        catch (err) {
            console.error(err);
        }
        finally {
            setShowFeedback(false);
        }
    }, [feedbackItem, authHeader, user?.email, selectedRule?.rule?.uuid]);
    const removeFeedback = useCallback(async () => {
        if (!authHeader) {
            setShowFeedback(false);
            return;
        }
        await deleteFeedback(authHeader, (feedbackItem as FeedbackItem).id);
        setFeedbackItem(null);
        setShowFeedback(false);
    }, [authHeader, (feedbackItem as FeedbackItem)?.id]);
  return { showFeedback, feedbackItem, submitFeedback, removeFeedback, setShowFeedback };
}
